import {MediaPlayer} from './MediaPlayer';
import { showToast } from '../../utils/toast.util';
import * as React from 'react';
import {useDropzone} from 'react-dropzone';
import {humanFileSize} from '../../../utils';
import {TranslateErrorDropzone} from '../../../utils/translator/TranslateErrorDropzone';
import {UploadMediaProps, FileProps} from '../../types';
import {ImageCropModal} from './ImageCropModal';
import {garantirPath, podeRecortar} from './imageCrop';

import {
  MediaBox, 
  MediaPreview, 
  MediaPreviewSubtitle, 
  MediaGroupContainer, 
  MediaPreviewContainer,
  MediaRemovePreviewButton
} from './MediaComponents.style';

import {CSSProperties} from "react";

const labelAligned: CSSProperties = {
  textAlign: "center",
}

const ACCEPT_MEDIA = {
  image: 'image/jpeg, image/png',
  video: 'video/*',
  audio: 'audio/*',
  attachment: 'application/zip, application/pdf'
};

const MAX_SIZE: Partial<Record<UploadMediaProps['mediaType'], number>> = {
  image: 10485760,
  video: 943718400,
  attachment: 5242880,
}

const ICON = {
  image: 'images',
  video: 'play',
  audio: 'podcast',
  attachment: 'file-pdf',
}

const UX_MESSAGE = {
  image: 'Clique para selecionar ou arraste sua imagem aqui.',
  video: 'Clique para selecionar ou arraste seu vídeo aqui.',
  audio: 'Clique para selecionar ou arraste seu áudio aqui.',
  attachment: 'Clique para selecionar ou arraste seu PDF aqui.'
}

export const UploadMediaFile = ( {setUploadFile, propFile, mediaType = 'image', customMessage, cropAspect, cropTitle, children} : UploadMediaProps) : JSX.Element => {
  const [files, setFiles] = React.useState<FileProps[]>([]);
  const [errors, setErrors] = React.useState('');
  const [paraRecortar, setParaRecortar] = React.useState<File | null>(null);

  React.useEffect(() => () => {
    files.forEach(file => {

      URL.revokeObjectURL(file.preview)
    });
  }, [files]);

  React.useEffect(()=>{
    if(propFile) setFiles([propFile])
  },[])

  // Sem `path` o thumb inteiro cai para o estado vazio, e o arquivo que sai do
  // recorte nasce sem ele.
  const aceitarArquivo = (arquivo: File) => {
    const comPreview: FileProps = Object.assign(garantirPath(arquivo), {
      preview: URL.createObjectURL(arquivo)
    });

    setFiles([comPreview]);
    setUploadFile(comPreview);
  }

  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject
  } = useDropzone({
    accept: ACCEPT_MEDIA[mediaType],
    maxFiles: 1,
    maxSize: MAX_SIZE[mediaType],
    onDrop: acceptedFiles => {
      const arquivo = acceptedFiles[0];
      if(!arquivo) return;

      // Toda imagem passa pelo recorte antes de virar upload; o que o usuario
      // enquadra e o que sobe.
      if(podeRecortar(arquivo.type)){
        setParaRecortar(arquivo);
        return;
      }

      aceitarArquivo(arquivo);
    }
  });

  const removeAll = () => {
    acceptedFiles.length = 0;
    acceptedFiles.splice(0, acceptedFiles.length);
    setFiles([]);
    setUploadFile({path: '', preview: ''});
  }

  const MediaPreviewImage = (file: FileProps & {uri?: string}) => {
    return(
        <MediaPreview>
          <img 
            src={file.preview || file.uri} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              borderRadius: '4px'
            }} 
          />
          {!!file?.path && !!file.size &&
            <MediaPreviewSubtitle>
              {file.path} - {humanFileSize(file.size)}
              <MediaRemovePreviewButton onClick={removeAll} title='Remover arquivo'/>
            </MediaPreviewSubtitle>
          }
        </MediaPreview>
    )
  }

  React.useEffect(() => {
    if(errors.length) showToast({html: errors})
  }, [errors])

  if(fileRejections.length) fileRejections.forEach(({errors: errs}) => {
    if(errs[0]){
      const message = TranslateErrorDropzone(errs[0], mediaType);
      if(message !== errors) setErrors(message)
    }
  })
  const MediaPreviewVideo = (file: FileProps & {uri?: string}) => {
    return <>
      <MediaPlayer src={file.preview || file.uri} mime={file.type} />
      {!!file?.path && !!file.size && <MediaPreviewSubtitle>
        {file.path} - {humanFileSize(file.size)}
        <MediaRemovePreviewButton onClick={removeAll} title="Remover arquivo" />
      </MediaPreviewSubtitle>}
    </>;
  }

  const MediaPreviewAudio = (file: FileProps & {uri?: string}) => {
    return(
      <>
        <MediaPlayer src={file.preview || file.uri} mime={file.type} kind="audio" />
      </>
    )
  }

  const MediaPreviewPdf = (file: FileProps & {uri?: string}) => {
      return(
        <>
          <MediaPreview>
            <object data={file.preview} width="100%" height="100%"></object>
          </MediaPreview>
          {!!file?.path && !!file.size &&
            <MediaPreviewSubtitle>
              {file.path} - {humanFileSize(file.size)}
              <MediaRemovePreviewButton onClick={removeAll} title='Remover arquivo' />
            </MediaPreviewSubtitle>
          }
        </>
      )
  }

  const MEDIA_PREVIEW = {
    image: (file: FileProps & {uri?: string}) => MediaPreviewImage(file),
    video: (file: FileProps & {uri?: string}) => MediaPreviewVideo(file),
    audio: (file: FileProps & {uri?: string}) => MediaPreviewAudio(file),
    attachment: (file: FileProps & {uri?: string}) => MediaPreviewPdf(file),
  }
  
  const thumbs = React.useMemo(() => files.map((file, index) => (
    <div key={file?.path || index}>
      {MEDIA_PREVIEW[mediaType](file)}
      {!!children && <span>
        {UX_MESSAGE[mediaType]}
      </span>}
    </div>
  )), [files, mediaType, children]);

  const renderEmptyPreview = () => {
    if(children) return children;
    return(
      <MediaPreview className='empty-preview'>
        <i className={`fal fa-${ICON[mediaType]}`}></i>
        <span>
          {UX_MESSAGE[mediaType]}
        </span>
      </MediaPreview>
    )
  }
  
  return(
    <MediaGroupContainer>
      <MediaPreviewContainer {...getRootProps({isDragActive, isDragAccept, isDragReject})} className='media-preview'>
        <section>
            {files.length && ((files[0] as FileProps & {uri?: string})?.uri || files[0].path) ? thumbs : renderEmptyPreview()}
            <input {...getInputProps()} />
        </section>
      </MediaPreviewContainer>

      {paraRecortar && (
        <ImageCropModal
          arquivo={paraRecortar}
          proporcao={cropAspect}
          titulo={cropTitle}
          onCancel={() => setParaRecortar(null)}
          onConfirm={(recortado) => {
            setParaRecortar(null);
            aceitarArquivo(recortado);
          }}
        />
      )}
    </MediaGroupContainer>
  );
}